import { ApiError } from '@app/api/ApiError';
import { readToken } from '@app/services/localStorage.service';
import { getConfig } from '../services/configService';
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { refreshTokenRequest, refreshTokenPath } from './token';
import { STATUS_CODES } from '@app/constants/statusCodes';
import { RootState, Store } from '@app/store/store';
import { logout, setTokens } from '@app/store/slices/token';
import { ITokens } from '@app/interfaces/token';
import { configBaseUrl } from './config';
import { normalizeApiError, shouldNotifyApiError } from './errorNormalizer';
import { beginSessionEnd, isSessionEnding, resetSessionEnd } from './sessionState';
import { notifyNormalizedApiError } from '@app/utils/notifyApiError';
import i18n from '@app/i18n';
import { captureApiError } from '@app/services/sentryService';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export const httpApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

httpApi.interceptors.request.use((config) => {
  config.headers = new AxiosHeaders({
    ...config.headers,
    Authorization: `Bearer ${readToken()}`,
  });

  return config;
});

httpApi.interceptors.response.use(undefined, (error: AxiosError) => {
  const data = error.response?.data as ApiErrorData;
  throw new ApiError<ApiErrorData>(data?.message || error.message, data);
});

export interface ApiErrorData {
  message: string;
}

const axiosClient = axios.create();

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const defaultAxiosHeaders = headers;

// CONFIG ====================================================
// Initialize with config - this will be called after loadConfig()
export const initializeAxiosClient = () => {
  const config = getConfig();
  axiosClient.defaults.baseURL = config.apiBaseUrl;
};

axiosClient.defaults.headers.common = AxiosHeaders.from({
  ...axiosClient.defaults.headers.common,
  ...headers,
});
axiosClient.defaults.timeout = 600000;
axiosClient.defaults.withCredentials = true;
// END CONFIG ================================================

/**
 * In-flight token refresh, shared by every request that 401s at the same moment.
 * Without it, refresh-token rotation invalidates the parallel refreshes.
 */
let refreshPromise: Promise<ITokens> | null = null;

const refreshTokensOnce = async (dispatch: typeof Store.dispatch): Promise<ITokens> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const responseOfRefreshToken = await refreshTokenRequest();
      const newTokens = responseOfRefreshToken.data as ITokens;

      dispatch(
        setTokens({
          accessToken: newTokens.accessToken,
          refreshToken: newTokens.refreshToken,
        }),
      );

      //   await saveTokensInSecureStorage({ TODO: add tokens to local storage
      //     accessToken: newTokens.accessToken,
      //     refreshToken: newTokens.refreshToken,
      //   });

      axiosClient.defaults.headers.common['Authorization'] = `Bearer ${newTokens.accessToken}`;
      resetSessionEnd();

      return newTokens;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

/**
 * Single place where a failed request becomes visible, so a request cannot fail
 * silently regardless of what its API module or call site does.
 */
const reportRequestFailure = (error: AxiosError, config?: CustomAxiosRequestConfig): void => {
  const normalized = normalizeApiError(error);
  // Sent even for `silentError` requests: a hidden failure is still worth a trace.
  // Requests killed by a logout/page unload are not.
  const referenceId = isSessionEnding() ? undefined : captureApiError(normalized, config);

  // Logged in every environment: the stack trace and error_code never reach the UI.
  const request = `${config?.method?.toUpperCase() ?? ''} ${config?.url ?? ''}`.trim();
  console.error(
    '[api] %s failed (%s %s)',
    request,
    normalized.status ?? normalized.errorCode ?? 'no status',
    normalized.errorCode ?? '',
    normalized.exceptionMessage ?? normalized.errorMessage ?? '',
  );

  if (!shouldNotifyApiError(normalized, config?.silentError)) {
    return;
  }

  notifyNormalizedApiError(i18n.t, normalized, referenceId);
};

export const interceptor = (store: typeof Store) => {
  axiosClient.interceptors.request.use(
    (conf) => {
      const state = store.getState() as RootState;
      const { accessToken, refreshToken } = state.token;
      const isRefreshTokenReq = conf.url === refreshTokenPath;
      const targetToken = isRefreshTokenReq ? refreshToken : accessToken;

      const isConfig = conf.url != null && conf.url?.includes(configBaseUrl);

      if (targetToken.length) {
        if (conf.headers && !isConfig) {
          conf.headers['Authorization'] = `Bearer ${targetToken}`;
        }
      }

      return conf;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    },
  );
  axiosClient.interceptors.response.use(
    (next) => {
      // A successful response means the app is alive: lift any leftover silence.
      if (isSessionEnding()) {
        resetSessionEnd();
      }
      return Promise.resolve(next);
    },
    async (error: AxiosError) => {
      const status = error?.response?.status;
      const dispatch = store.dispatch;
      const currentResponseHeader = (error?.response?.config.headers ?? {}) as AxiosRequestHeaders;
      const originalRequest = error.config as CustomAxiosRequestConfig;

      if (status === STATUS_CODES.UNAUTHORIZED && currentResponseHeader['Authorization']) {
        if (originalRequest.url === refreshTokenPath) {
          // Session end is silent: the login page is the signal.
          beginSessionEnd();
          void dispatch(logout());
          return Promise.reject(error);
        } else if (originalRequest.url?.includes('/web/admin/password')) {
          // Don't retry password change requests - they may invalidate the session
          return Promise.reject(error);
        } else if (!originalRequest._retry) {
          originalRequest._retry = true;

          // Logging out on a failed refresh, so the user is not left on a dead session.
          try {
            await refreshTokensOnce(dispatch);
          } catch (refreshError) {
            beginSessionEnd();
            void dispatch(logout());
            return Promise.reject(refreshError);
          }

          // Outside the try/catch so a failing retry is reported as its own error.
          return axiosClient(originalRequest);
        }
      }

      reportRequestFailure(error, originalRequest);

      return Promise.reject(error);
    },
  );
};

export const api = axiosClient;
